import { useState } from "react";
import {
  ArrowRight,
  ArrowUpRight,
  Bot,
  Check,
  ClipboardList,
  Globe2,
  Headphones,
  CalendarDays,
  MessageSquare,
  PhoneMissed,
  Workflow,
  Users,
} from "lucide-react";
import { Link } from "./Navigation";
import "./inner-pages.css";

// The seven things we sell, in the order /solutions prints them. Each path is
// an anchor on that one page rather than a route of its own: the seven product
// routes were retired on 2026-09-07 and 301 into these anchors.
export const SOLUTIONS = [
  {
    name: "AI receptionist",
    path: "/solutions#ai-receptionist",
    text: "Every call answered in your words.",
    icon: Headphones,
  },
  {
    name: "CRM",
    path: "/solutions#crm",
    text: "Who they are and what happens next, in one place.",
    icon: Users,
  },
  {
    name: "Missed-call recovery",
    path: "/solutions#missed-call-recovery",
    text: "Nobody waits in a voicemail box.",
    icon: PhoneMissed,
  },
  {
    name: "AI employee",
    path: "/solutions#ai-employee",
    text: "Voice, chat, and the work that follows.",
    icon: Bot,
  },
  {
    name: "CRM & calendar",
    path: "/solutions#crm-calendar",
    text: "Keep the person and the appointment together.",
    icon: CalendarDays,
  },
  {
    name: "Websites",
    path: "/solutions#websites",
    text: "A first impression that takes a booking.",
    icon: Globe2,
  },
  {
    name: "Automated workflows",
    path: "/solutions#workflows",
    text: "Make the follow-through repeatable.",
    icon: Workflow,
  },
];
// The directory uses the product ledger's grammar: hairline rules, type on
// space, no icon-heading-text rows repeated four times. On /solutions it is
// the page's own index, so it names the sections below it rather than pointing
// off to other pages.
export function SolutionDirectory() {
  return (
    <section className="ip-solutions rb-wrap" data-sc-act="flow">
      <div className="ip-solutions-head" data-sc-in data-sc-stagger="60">
        <h2>
          Seven things,
          <br />
          one page.
        </h2>
        <p>Jump to the one you came for.</p>
      </div>
      <div
        className="rb-ledger ip-solutions-ledger"
        data-sc-in
        data-sc-stagger="60"
      >
        {/* Same-page anchors, so a plain <a> rather than the router Link that
            reloads the document on every internal navigation. */}
        {SOLUTIONS.map((s) => (
          <a href={s.path} key={s.path}>
            <span>{s.name}</span>
            <p>{s.text}</p>
            <ArrowUpRight />
          </a>
        ))}
      </div>
    </section>
  );
}

const PROCESS = [
  {
    name: "Understand",
    icon: ClipboardList,
    title: "Start with a real day at work.",
    body: "We review the calls you get, the work your team does, and where a customer can get stuck. Bring your current phone, calendar and CRM to that review.",
    fields: [
      ["Bring", "Services, hours, and current tools"],
      ["Decide", "The first problem to solve"],
      ["Leave with", "A defined setup scope"],
    ],
  },
  {
    name: "Configure",
    icon: Workflow,
    title: "Give the system your ground rules.",
    body: "Approved answers, booking rules, service areas, and human handoffs turn a general tool into a front office that represents your business.",
    fields: [
      ["Knowledge", "Your approved business information"],
      ["Availability", "Your appointment and booking rules"],
      ["Handoff", "The situations your people handle"],
    ],
  },
  {
    name: "Test",
    icon: Headphones,
    title: "Hear it before your customers do.",
    body: "Test calls help expose unclear answers and awkward transitions. The team reviews those scenarios and tunes the flow before it represents you.",
    fields: [
      ["Try", "Common questions and edge cases"],
      ["Review", "Answers, routing, and booking flow"],
      ["Refine", "Instructions that need more clarity"],
    ],
  },
  {
    name: "Review",
    icon: Users,
    title: "Keep people in the picture.",
    body: "Your team can review call records and bring changes back into the setup. When the business changes, the instructions and workflow should reflect it.",
    fields: [
      ["See", "Call records and customer context"],
      ["Discuss", "New services or changing processes"],
      ["Update", "The agreed business rules"],
    ],
  },
];
export function SetupProcess() {
  const [step, setStep] = useState(0);
  const s = PROCESS[step];
  return (
    <section className="ip-setup rb-wrap">
      <header>
        <h2>
          Thought through.
          <br />
          Then switched on.
        </h2>
        <p>
          A clear path from your first call with us to a front office built
          around your business.
        </p>
      </header>
      <div className="ip-setup-surface">
        <div
          className="ip-process-tabs"
          role="group"
          aria-label="Explore setup stages"
        >
          {PROCESS.map((p, i) => (
            <button
              key={p.name}
              aria-pressed={i === step}
              onClick={() => setStep(i)}
            >
              <p.icon size={22} />
              {p.name}
              <ArrowRight size={18} />
            </button>
          ))}
        </div>
        <div className="ip-process-body" aria-live="polite" aria-atomic="true">
          <div>
            <s.icon size={38} strokeWidth={1.3} />
            <h3>{s.title}</h3>
            <p>{s.body}</p>
            <Link to="/book" className="rb-text-link">
              Talk through your setup <ArrowUpRight size={17} />
            </Link>
          </div>
          <dl>
            {s.fields.map(([term, value]) => (
              <div key={term}>
                <dt>{term}</dt>
                <dd>
                  {value}
                  <Check size={17} />
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </section>
  );
}

export function RecoveryConversation() {
  const [step, setStep] = useState(0);
  const messages = [
    ["Missed call", "A customer calls while your team is on a job."],
    [
      "Follow-up",
      "Thanks for calling. Sorry we missed you. What can we help you with?",
    ],
    [
      "Customer reply",
      "I’d like to arrange an estimate for some work at my property.",
    ],
    [
      "Next step",
      "Capture the request, offer the approved booking path, and keep it all on one contact record.",
    ],
  ];
  return (
    <section className="ip-recovery-demo rb-wrap">
      <div>
        <h2>
          The call ended.
          <br />
          The line
          <br />
          doesn’t have to.
        </h2>
        <p>
          A configured follow-up gives the customer another way to explain what
          they need.
        </p>
        <div
          className="ip-step-list"
          role="group"
          aria-label="Explore missed-call recovery"
        >
          {messages.map(([label], i) => (
            <button
              key={label}
              onClick={() => setStep(i)}
              aria-pressed={step === i}
            >
              <span>{label}</span>
              <ArrowRight size={18} />
            </button>
          ))}
        </div>
      </div>
      <div className="ip-message-thread" aria-live="polite">
        <div className="ip-transcript-head">
          <MessageSquare size={22} />
          <span>A second chance to connect</span>
        </div>
        <p className="ip-example-note">
          Illustrative SMS workflow. Timing and messages depend on your
          configuration.
        </p>
        {messages.slice(0, step + 1).map(([label, body], i) => (
          <div className={`ip-message ip-message-${i}`} key={label}>
            <span>{label}</span>
            <p>{body}</p>
          </div>
        ))}
        <div className="ip-message-link">
          <Users size={20} />
          <span>One contact. Every message in one place.</span>
        </div>
      </div>
    </section>
  );
}

export function BuyerQuestions() {
  const [active, setActive] = useState(0);
  const questions = [
    [
      "What should it handle?",
      "Start with the actual calls: routine questions, new inquiries, booking requests, and the situations that need a person.",
    ],
    [
      "What does it connect to?",
      "Bring your phone setup, calendar, CRM, and website. We confirm compatibility and scope before defining the integration.",
    ],
    [
      "What will the full cost be?",
      "Compare the subscription, usage, setup scope, and any custom work. The published Chleb plans show software prices; voice and SMS usage are separate.",
    ],
    [
      "Who keeps it useful?",
      "Decide who reviews records, updates business information, and raises a call-flow issue. Clear ownership matters after launch too.",
    ],
  ];
  return (
    <section className="ip-buyer rb-wrap">
      <h2>
        Good questions.
        <br />
        Better decisions.
      </h2>
      <div>
        <div
          className="ip-buyer-options"
          role="group"
          aria-label="Questions to ask before choosing"
        >
          {questions.map(([q], i) => (
            <button
              key={q}
              aria-pressed={active === i}
              onClick={() => setActive(i)}
            >
              {q}
              <ArrowUpRight size={18} />
            </button>
          ))}
        </div>
        <p className="ip-buyer-answer" aria-live="polite">
          {questions[active][1]}
        </p>
        <Link className="rb-text-link" to="/comparisons">
          Explore your options <ArrowRight size={18} />
        </Link>
      </div>
    </section>
  );
}
